import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MsalService } from '@azure/msal-angular';
import { TranslocoPipe } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { PositionShare } from '../../../../api/app-types/user.app';
import { POSITION_SHARE_SERVICE } from '../../../../api/services/position-share.service';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { Paginator } from '../../../../core/components/paginator/paginator';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { createErrorNotification } from '../../../../shared/notification/notification.factory';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { LocalStorageService } from '../../../../shared/storage/local-storage.service';

@Component({
	selector: 'lvr-position-share-list',
	imports: [TranslocoPipe, MatTableModule, MatIconButton, MatIcon, DatePipe, Paginator],
	templateUrl: './position-share-list.html',
	styleUrl: './position-share-list.scss',
})
export class PositionShareList {
	private readonly env = inject(ENVIRONMENT);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });
	private readonly positionShareService = inject(POSITION_SHARE_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);

	public readonly userId = input<number>();
	public readonly canEditPositionShares = input<boolean>(true);
	public readonly editShare = output<PositionShare>();

	private readonly positionSharePage = signal<PaginatedResponse<PositionShare>>({
		entities: [],
		offset: 0,
		range: 0,
		totalEntities: 0,
	});

	public readonly positionShares = computed(() => this.positionSharePage().entities);
	public readonly totalPositionShares = computed(() => this.positionSharePage().totalEntities);

	public readonly displayedColumns = computed(() =>
		this.canEditPositionShares() ? ['share', 'start', 'end', 'actions'] : ['share', 'start', 'end'],
	);
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;

	public readonly loading = signal(false);
	public readonly pageIndex = signal(0);
	public readonly pageSize = signal(this.env.defaultPageSize);
	private readonly restoredUserId = signal<number | undefined>(undefined);

	public constructor() {
		effect(() => {
			const userId = this.userId();
			if (userId == null) return;

			if (this.restoredUserId() !== userId) {
				const savedPageSize = this.getSavedPageSize('position-share-list');
				this.pageIndex.set(0);
				this.pageSize.set(savedPageSize ?? this.env.defaultPageSize);
				this.restoredUserId.set(userId);
				return;
			}

			this.loadData(userId, this.pageIndex(), this.pageSize());
		});
	}

	public handlePageEvent = (event: PageEvent) => {
		this.pageIndex.set(event.pageIndex);
		this.pageSize.set(event.pageSize);
		this.savePageSize('position-share-list', event.pageSize);
		if (this.userId() != null) this.loadData(this.userId()!, this.pageIndex(), this.pageSize());
	};

	private async loadData(uid: number, pageIndex: number, pageSize: number) {
		this.loading.set(true);
		try {
			const res = await this.positionShareService.getAll(uid, pageIndex, pageSize);
			this.positionSharePage.set(res);
		} catch {
			this.notificationService.addNotification(createErrorNotification('Fehler beim Neuladen der Stellenanteilliste'));
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * Checks if the given date string is valid and different from the default DateOnly.MinValue ('0001-01-01'),
	 * which is returned by the backend when no date is provided.
	 * @param input - Date string to validate.
	 * @returns true if the date is valid and not the default min value.
	 */
	public isValidDate(input?: Date | string) {
		if (!input) {
			return false;
		}

		const date = new Date(input);
		if (Number.isNaN(date.getTime())) {
			return false;
		}

		// Check if it's the default '0001-01-01'
		const isMinDate = date.getFullYear() === 1 && date.getMonth() === 0 && date.getDate() === 1;

		return !isMinDate;
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}

	private getSavedPageSize(preferenceKey: string): number | undefined {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return undefined;

		const saved = this.storage.getPageSize(userId, preferenceKey);
		if (!saved || !this.env.pageSizeOptions.includes(saved)) return undefined;
		return saved;
	}

	private savePageSize(preferenceKey: string, pageSize: number) {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return;
		this.storage.setPageSize(userId, preferenceKey, pageSize);
	}
}
